/**
 * Comisiones de los profesionales: porcentaje sobre lo COBRADO (descontando laboratorio y materiales) o valor fijo por procedimiento
 * (para especialistas externos), liquidadas por quincena y aprobadas por la administración. Lógica pura, sin base de datos.
 * Todo el dinero va en centavos.
 */
import { sumarDias } from "./agenda.ts";
import { valorItem } from "./plan.ts";
import { norm } from "./texto.ts";

/* ---------- tipos de tratamiento ---------- */

export const TIPOS_TRATAMIENTO = [
  "Prevención y diagnóstico", "Operatoria", "Endodoncia", "Periodoncia", "Cirugía", "Implantes", "Rehabilitación", "Ortodoncia", "Odontopediatría", "Estética", "Otros",
] as const;

const POR_CODIGO: Readonly<Record<string, (typeof TIPOS_TRATAMIENTO)[number]>> = {
  CON: "Prevención y diagnóstico", RX: "Prevención y diagnóstico", PRO: "Prevención y diagnóstico", PRS: "Prevención y diagnóstico", IHO: "Prevención y diagnóstico", FLU: "Prevención y diagnóstico", SEL: "Prevención y diagnóstico",
  RAS: "Periodoncia", TIN: "Periodoncia", ABP: "Periodoncia", GNG: "Periodoncia", CPC: "Periodoncia", INJ: "Periodoncia", MPE: "Periodoncia",
  RES1: "Operatoria", RES2: "Operatoria", RES3: "Operatoria", ION: "Operatoria", AMA: "Operatoria", INC: "Operatoria",
  CV: "Estética", BLA: "Estética",
  END1: "Endodoncia", END2: "Endodoncia", END3: "Endodoncia",
  PPT: "Odontopediatría", PPC: "Odontopediatría", CA: "Odontopediatría", ME: "Odontopediatría",
  EXO: "Cirugía", EXQ: "Cirugía", QUI: "Cirugía", APX: "Cirugía", VEN: "Cirugía", FRE: "Cirugía", ALV: "Cirugía",
  IMP: "Implantes",
  PER: "Rehabilitación", COR: "Rehabilitación", CZR: "Rehabilitación", PF: "Rehabilitación", PR: "Rehabilitación", PT: "Rehabilitación",
};

/** Tipo de tratamiento de un procedimiento: por su código del arancel o, si es propio de la clínica, por su nombre. */
export function tipoTratamiento(codigo: string, nombre = ""): string {
  const base = POR_CODIGO[codigo.toUpperCase()];
  if (base) return base;
  const n = norm(nombre);
  if (/ortodon|bracket|alineador|retenedor|contencion/.test(n)) return "Ortodoncia";
  if (/implante/.test(n)) return "Implantes";
  if (/endodon|conducto/.test(n)) return "Endodoncia";
  if (/extraccion|exodon|cirug|quiste|apicect|frenect/.test(n)) return "Cirugía";
  if (/corona|puente|protesis|perno/.test(n)) return "Rehabilitación";
  if (/resina|obturacion|amalgama|ionomero/.test(n)) return "Operatoria";
  if (/blanqueamiento|carilla/.test(n)) return "Estética";
  if (/periodon|raspado/.test(n)) return "Periodoncia";
  if (/profilaxis|limpieza|consulta|radiograf|fluor|sellante/.test(n)) return "Prevención y diagnóstico";
  return "Otros";
}

/* ---------- reglas ---------- */

export interface ReglaComision {
  ambito: "general" | "tipo" | "codigo";
  /** vacío en «general»; un tipo de tratamiento en «tipo»; el código del arancel en «codigo» */
  clave: string;
  modo: "porcentaje" | "fijo";
  /** 0 a 100 (modo porcentaje) */
  porcentaje: number;
  /** centavos por procedimiento (modo fijo, p. ej. un cirujano externo que cobra $45 por extracción) */
  fijo: number;
  /** solo en «fijo»: se genera al realizar el procedimiento o solo en proporción a lo cobrado */
  cuando: "cobrado" | "realizado";
}

export function problemasRegla(r: ReglaComision): string[] {
  const e: string[] = [];
  if (!["general", "tipo", "codigo"].includes(r.ambito)) e.push("Elige a qué se aplica la regla.");
  if (r.ambito === "tipo" && !(TIPOS_TRATAMIENTO as readonly string[]).includes(r.clave)) e.push("Elige un tipo de tratamiento.");
  if (r.ambito === "codigo" && !/^[A-Z0-9]{1,10}$/.test(r.clave)) e.push("El código del procedimiento no es válido.");
  if (r.modo === "porcentaje" && (!Number.isFinite(r.porcentaje) || r.porcentaje < 0 || r.porcentaje > 100)) e.push("El porcentaje va de 0 a 100.");
  if (r.modo === "fijo" && (!Number.isInteger(r.fijo) || r.fijo < 0 || r.fijo > 100_000_00)) e.push("El valor fijo no es válido.");
  if (!["porcentaje", "fijo"].includes(r.modo)) e.push("Elige porcentaje o valor fijo.");
  return e;
}

/** La regla que manda: la del código del procedimiento; si no, la de su tipo de tratamiento; si no, la general. */
export function reglaPara(reglas: readonly ReglaComision[], codigo: string, nombre = ""): ReglaComision | null {
  const cod = codigo.toUpperCase();
  return reglas.find((r) => r.ambito === "codigo" && r.clave === cod)
    ?? reglas.find((r) => r.ambito === "tipo" && r.clave === tipoTratamiento(cod, nombre))
    ?? reglas.find((r) => r.ambito === "general")
    ?? null;
}

export const textoRegla = (r: ReglaComision | null) =>
  !r ? "sin regla" : r.modo === "porcentaje" ? `${r.porcentaje} %` : `$${(r.fijo / 100).toFixed(2)} fijo${r.cuando === "cobrado" ? " al cobrar" : ""}`;

/* ---------- quincenas ---------- */

export interface Quincena { desde: string; hasta: string; nombre: string }
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** Quincena que contiene la fecha: del 1 al 15 o del 16 al fin de mes. */
export function quincenaDe(fecha: string): Quincena {
  const a = +fecha.slice(0, 4), m = +fecha.slice(5, 7), d = +fecha.slice(8, 10);
  const primera = d <= 15;
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  const pre = fecha.slice(0, 8);
  return { desde: `${pre}${primera ? "01" : "16"}`, hasta: `${pre}${primera ? "15" : String(ultimo).padStart(2, "0")}`, nombre: `${primera ? "1.ª" : "2.ª"} quincena de ${MESES[m - 1]} de ${a}` };
}
export const quincenaAnterior = (q: Quincena) => quincenaDe(sumarDias(q.desde, -1));
export const quincenaSiguiente = (q: Quincena) => quincenaDe(sumarDias(q.hasta, 1));

/* ---------- cálculo ---------- */

export interface ItemCom {
  id: string; pacienteId: string; paciente: string; codigo: string; descripcion: string; diente: number | null;
  cantidad: number; precio: number; descAplicado: number | null; realizadoEn: string; realizadoPor: string;
  costoLab: number; costoMat: number;
  /** centavos que el paciente ya había pagado de este procedimiento antes de usar Donna (tratamientos anteriores) */
  pagadoAntes?: number;
  /** valor de lista (sin IVA) de un cupo gratis del club que la clínica asume: genera comisión como si estuviera cobrado */
  valorClub?: number;
}
/** Un abono vigente (no anulado). `orden` desempata abonos del mismo día (el número de recibo). */
export interface AbonoCom { pacienteId: string; fecha: string; monto: number; orden: number; /** si se abonó directamente sobre un procedimiento, cubre primero ese */ itemId?: string | null }

export interface LineaCom {
  itemId: string; pacienteId: string; paciente: string; codigo: string; descripcion: string; diente: number | null; realizadoEn: string; cantidad: number;
  /** valor del procedimiento sin IVA, con el descuento que tenía al realizarse */
  valorNeto: number; costoLab: number; costoMat: number;
  /** cuánto de su valor (con IVA) está cobrado a la fecha, de 0 a 1 */
  fraccionCobrada: number;
  regla: ReglaComision | null;
  /** valor sobre el que se aplica el porcentaje: (valor − laboratorio − materiales) × fracción cobrada */
  base: number;
  /** comisión total que corresponde a este procedimiento a la fecha */
  acumulado: number;
}

/**
 * Comisión de cada procedimiento realizado por `usuarioId` hasta `hasta`, sobre lo cobrado.
 * Los abonos de cada paciente cubren sus procedimientos realizados en orden (el más antiguo primero), como en una cuenta corriente:
 * un abono parcial genera comisión parcial. El IVA no entra en la base.
 */
export function comisionesAFecha(x: { items: readonly ItemCom[]; abonos: readonly AbonoCom[]; ivaPct: number; hasta: string; usuarioId: string; reglas: readonly ReglaComision[] }): LineaCom[] {
  const res: LineaCom[] = [];
  const porPaciente = new Map<string, ItemCom[]>();
  for (const i of x.items) if (i.realizadoEn <= x.hasta) porPaciente.set(i.pacienteId, [...(porPaciente.get(i.pacienteId) ?? []), i]);
  for (const [pid, lista] of porPaciente) {
    const delPaciente = x.abonos.filter((a) => a.pacienteId === pid && a.fecha <= x.hasta);
    let resto = delPaciente.filter((a) => !a.itemId).reduce((s, a) => s + a.monto, 0);
    const vinculado = new Map<string, number>();
    for (const a of delPaciente) if (a.itemId) vinculado.set(a.itemId, (vinculado.get(a.itemId) ?? 0) + a.monto);
    // lo abonado de más sobre un procedimiento pasa al pozo general
    const propio = new Map<string, number>();
    for (const i of lista) {
      const vn = valorItem({ precio: i.precio, cantidad: i.cantidad, estado: "realizado", descAplicado: i.descAplicado }, 0);
      const antes = Math.min(Math.round(vn * (1 + x.ivaPct / 100)), Math.max(0, i.pagadoAntes ?? 0));
      const suyo = Math.min(Math.max(0, Math.round(vn * (1 + x.ivaPct / 100)) - antes), vinculado.get(i.id) ?? 0);
      resto += (vinculado.get(i.id) ?? 0) - suyo;
      propio.set(i.id, suyo);
    }
    for (const i of [...lista].sort((a, b) => a.realizadoEn.localeCompare(b.realizadoEn) || a.id.localeCompare(b.id))) {
      const valorNeto = valorItem({ precio: i.precio, cantidad: i.cantidad, estado: "realizado", descAplicado: i.descAplicado }, 0);
      const conIva = Math.round(valorNeto * (1 + x.ivaPct / 100));
      const pagadoAntes = Math.min(conIva, Math.max(0, i.pagadoAntes ?? 0));
      const suyo = propio.get(i.id) ?? 0;
      const asignado = suyo + Math.min(Math.max(0, conIva - pagadoAntes - suyo), Math.max(0, resto));
      resto -= asignado - suyo;
      if (i.realizadoPor !== x.usuarioId) continue;
      const fraccionPaciente = conIva > 0 ? (asignado + pagadoAntes) / conIva : 1;
      // un cupo gratis del club lo asume la clínica: ese valor cuenta como cobrado al realizar el procedimiento
      const club = Math.max(0, i.valorClub ?? 0);
      const totalCom = valorNeto + club;
      const fraccionCobrada = totalCom > 0 ? (valorNeto * fraccionPaciente + club) / totalCom : 1;
      const regla = reglaPara(x.reglas, i.codigo, i.descripcion);
      const base = Math.round(Math.max(0, totalCom - i.costoLab - i.costoMat) * fraccionCobrada);
      const acumulado = !regla ? 0
        : regla.modo === "porcentaje" ? Math.round(base * regla.porcentaje / 100)
        : Math.round(regla.fijo * i.cantidad * (regla.cuando === "cobrado" ? fraccionCobrada : 1));
      res.push({ itemId: i.id, pacienteId: pid, paciente: i.paciente, codigo: i.codigo, descripcion: i.descripcion, diente: i.diente, realizadoEn: i.realizadoEn, cantidad: i.cantidad,
        valorNeto: totalCom, costoLab: i.costoLab, costoMat: i.costoMat, fraccionCobrada, regla, base, acumulado });
    }
  }
  return res.sort((a, b) => a.realizadoEn.localeCompare(b.realizadoEn) || a.paciente.localeCompare(b.paciente));
}

export interface LineaLiquidar extends LineaCom { yaLiquidado: number; aLiquidar: number }

/** Lo que falta liquidar de cada procedimiento: lo que corresponde a la fecha menos lo ya liquidado (si se anuló un abono sale negativo). */
export function aLiquidar(lineas: readonly LineaCom[], yaLiquidado: ReadonlyMap<string, number>): LineaLiquidar[] {
  return lineas.map((l) => { const ya = yaLiquidado.get(l.itemId) ?? 0; return { ...l, yaLiquidado: ya, aLiquidar: l.acumulado - ya }; });
}
